import { Injectable, inject, signal, computed } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { ConfirmationService } from 'primeng/api';
import { ServicioSapRepository } from 'src/app/domain/service/servicio-sap/servicio-sap-repository';
import {
  ServicioSap,
  ServicioSapDireccion,
  ServicioSapFiltro
} from 'src/app/domain/model/servicio-sap/servicio-sap';
import {
  DEFAULT_ROWS_PER_PAGE,
  SERVICIO_SAP_CODIGO_MAX_LENGTH,
  SERVICIO_SAP_DESCRIPCION_MAX_LENGTH,
  SERVICIO_SAP_DESCRIPCION_REGEX
} from 'src/app/common/constants-ejb';
import { sanitizeText } from 'src/app/common/input-utils';

export type ServicioSapMensajeTipo = 'success' | 'info' | 'warning' | 'error';
export type ServicioSapModoDialogo = 'crear' | 'editar';

/** Valores que entrega el modal al pulsar Aceptar. */
export interface ServicioSapFormulario {
  cdservicio: string;
  dsservicio: string;
}

/**
 * Facade de "Administración › Maestro › Servicios SAP" (HU1287272).
 *
 * <p>Maneja el estado del listado (filtros, resultados, paginación por cursor), del modal de
 * crear/modificar y de la baja con confirmación. Expone solo estado (signals) y métodos al
 * container (patrón Facade + Container/Dumb, frontend-girop §14).</p>
 *
 * <p>Legacy: AdminServiciosSapActivity. Tras crear, modificar o eliminar con éxito el legacy cierra
 * el modal, muestra el mensaje de éxito y limpia la tabla de resultados.</p>
 *
 * <p>Los mensajes de negocio son literales del legacy (WidgetsNamesConstants.properties). El texto de
 * los errores 409 NO se toma del servidor: se usan estas constantes, y cualquier otro error muestra el
 * mensaje genérico de fallas técnicas sin detalle (RN-12).</p>
 */
@Injectable()
export class ServiciosSapFacade {
  static readonly MSG_SIN_RESULTADOS =
    'No se recuperó información con los parámetros ingresados.';
  static readonly MSG_ERROR_TECNICO =
    'Estimado usuario, en estos momentos estamos presentando fallas técnicas. Por favor contacte al administrador.';
  static readonly MSG_CAMPOS_INVALIDOS =
    'Por favor ingrese correctamente los siguientes campos';
  static readonly MSG_YA_EXISTE =
    'El registro que está intentando guardar ya existe';
  static readonly MSG_SERVICIOS_ASOCIADOS =
    'No es posible eliminar el registro debido a que existen servicios con el código seleccionado asociado.';
  static readonly MSG_INGRESO_EXITOSO =
    'Los datos se almacenaron correctamente.';
  static readonly MSG_ACTUALIZACION_EXITOSA =
    'Los datos se actualizaron correctamente.';
  static readonly MSG_BORRADO_EXITOSO =
    'Los datos se eliminaron correctamente.';
  static readonly CAMPO_CODIGO = 'Código servicio';
  static readonly CAMPO_DESCRIPCION = 'Descripción del servicio';

  private static readonly REGEX_CODIGO = new RegExp(
    `^[0-9]{1,${SERVICIO_SAP_CODIGO_MAX_LENGTH}}$`
  );
  private static readonly HTTP_CONFLICT = 409;

  private readonly repository = inject(ServicioSapRepository);
  private readonly confirmationService = inject(ConfirmationService);

  // ==================== Filtros ====================

  filtro: ServicioSapFiltro = {};

  // ==================== Resultado / estado ====================

  readonly items = signal<ServicioSap[]>([]);
  readonly loading = signal(false);
  readonly busquedaRealizada = signal(false);
  readonly mensaje = signal('');
  readonly mensajeTipo = signal<ServicioSapMensajeTipo>('info');

  // ==================== Paginación cursor (server-side) ====================

  readonly currentPage = signal(1);
  /** El legacy muestra 10 registros por página (RN-11); no es configurable en pantalla. */
  readonly pageSize = signal(DEFAULT_ROWS_PER_PAGE);
  readonly pageSizeOptions = [DEFAULT_ROWS_PER_PAGE];
  readonly totalElements = signal(0);
  private nextCursorActual: string | null = null;
  private previousCursorActual: string | null = null;

  readonly totalPages = computed(
    () => Math.ceil(this.totalElements() / this.pageSize()) || 1
  );
  // frontend-girop §17.7: la navegación la habilitan los flags de la RESPUESTA del servidor.
  readonly hasPrevious = signal(false);
  readonly hasNext = signal(false);
  readonly registroInicio = computed(() => {
    if (this.totalElements() === 0) return 0;
    return (this.currentPage() - 1) * this.pageSize() + 1;
  });
  readonly registroFin = computed(() =>
    Math.min(this.currentPage() * this.pageSize(), this.totalElements())
  );

  // ==================== Modal crear / modificar ====================

  readonly dialogoVisible = signal(false);
  readonly modoDialogo = signal<ServicioSapModoDialogo>('crear');
  readonly servicioEditando = signal<ServicioSap | null>(null);
  readonly guardando = signal(false);
  readonly mensajeDialogo = signal('');
  readonly mensajeDialogoTipo = signal<ServicioSapMensajeTipo>('warning');

  // ==================== Consulta ====================

  async buscar(): Promise<void> {
    if (this.loading()) return;
    if (!this.filtroValido()) return;
    this.busquedaRealizada.set(true);
    await this.cargarPagina(null, 'NEXT', 1);
  }

  limpiarFiltros(): void {
    this.filtro = {};
    this.reiniciarResultados();
    this.mensaje.set('');
  }

  irPrimeraPagina(): void {
    if (this.loading()) return;
    void this.cargarPagina(null, 'NEXT', 1);
  }

  irUltimaPagina(): void {
    if (this.loading()) return;
    void this.cargarPagina(null, 'PREVIOUS', this.totalPages(), true);
  }

  irPaginaAnterior(): void {
    if (this.loading() || !this.hasPrevious()) return;
    void this.cargarPagina(this.previousCursorActual, 'PREVIOUS', Math.max(1, this.currentPage() - 1));
  }

  irPaginaSiguiente(): void {
    if (this.loading() || !this.hasNext()) return;
    void this.cargarPagina(this.nextCursorActual, 'NEXT', this.currentPage() + 1);
  }

  /** {@code pagina} solo alimenta las etiquetas y se aplica tras la respuesta exitosa (§17.7). */
  private async cargarPagina(
    cursor: string | null,
    direction: ServicioSapDireccion,
    pagina: number,
    ultimaPagina = false
  ): Promise<void> {
    this.loading.set(true);
    this.mensaje.set('');
    try {
      const size = this.pageSize();
      const filtro: ServicioSapFiltro = {
        codigo: this.filtro.codigo?.trim(),
        descripcion: sanitizeText(this.filtro.descripcion)
      };
      const response = await this.repository.buscar(
        filtro,
        size,
        cursor,
        direction
      );

      this.items.set(response.content);
      this.totalElements.set(response.totalElements ?? response.content.length);
      this.nextCursorActual = response.nextCursor ?? null;
      this.previousCursorActual = response.previousCursor ?? null;
      this.hasNext.set(!!response.hasNext);
      this.hasPrevious.set(!!response.hasPrevious);
      const total = this.totalElements();
      this.currentPage.set(
        ultimaPagina ? (size > 0 && total > 0 ? Math.ceil(total / size) : 1) : pagina
      );

      if (response.content.length === 0) {
        // Legacy: cons.busquedaNoArrojoResultado() — la tabla queda oculta.
        this.busquedaRealizada.set(false);
        this.mostrarMensaje(ServiciosSapFacade.MSG_SIN_RESULTADOS, 'warning');
      }
    } catch {
      // Legacy: cons.genericError() — sin detalle técnico (RN-12).
      this.reiniciarResultados();
      this.mostrarMensaje(ServiciosSapFacade.MSG_ERROR_TECNICO, 'error');
    } finally {
      this.loading.set(false);
    }
  }

  // ==================== Modal ====================

  abrirNuevo(): void {
    this.modoDialogo.set('crear');
    this.servicioEditando.set(null);
    this.limpiarMensajeDialogo();
    this.dialogoVisible.set(true);
  }

  abrirEditar(servicio: ServicioSap): void {
    this.modoDialogo.set('editar');
    this.servicioEditando.set(servicio);
    this.limpiarMensajeDialogo();
    this.dialogoVisible.set(true);
  }

  cerrarDialogo(): void {
    this.dialogoVisible.set(false);
    this.servicioEditando.set(null);
    this.limpiarMensajeDialogo();
  }

  /**
   * Valida y guarda. Con datos inválidos o con un código duplicado el modal permanece abierto con el
   * mensaje (Escenarios 2 y 11); en cualquier otro caso el modal se cierra.
   */
  async guardar(formulario: ServicioSapFormulario): Promise<void> {
    if (this.guardando()) return;
    const modo = this.modoDialogo();
    const cdservicio =
      (modo === 'editar'
        ? this.servicioEditando()?.cdservicio
        : formulario.cdservicio
      )?.trim() ?? '';
    const dsservicio = sanitizeText(formulario.dsservicio).toUpperCase();

    const camposInvalidos = this.camposInvalidos(modo, cdservicio, dsservicio);
    if (camposInvalidos.length > 0) {
      this.mensajeDialogo.set(
        `${ServiciosSapFacade.MSG_CAMPOS_INVALIDOS}: ${camposInvalidos.join(', ')}`
      );
      this.mensajeDialogoTipo.set('warning');
      return;
    }

    this.guardando.set(true);
    this.limpiarMensajeDialogo();
    try {
      if (modo === 'crear') {
        await this.repository.crear({ cdservicio, dsservicio });
      } else {
        await this.repository.actualizar(cdservicio, dsservicio);
      }
      this.cerrarDialogo();
      this.reiniciarResultados();
      this.mostrarMensaje(
        modo === 'crear'
          ? ServiciosSapFacade.MSG_INGRESO_EXITOSO
          : ServiciosSapFacade.MSG_ACTUALIZACION_EXITOSA,
        'success'
      );
    } catch (error) {
      if (modo === 'crear' && this.esConflicto(error)) {
        // Legacy: cons.recordAlreadyExists() dentro del modal; el modal sigue abierto.
        this.mensajeDialogo.set(ServiciosSapFacade.MSG_YA_EXISTE);
        this.mensajeDialogoTipo.set('warning');
      } else {
        // Legacy: processResultCommand cierra el modal y muestra el error en la pantalla principal.
        this.cerrarDialogo();
        this.reiniciarResultados();
        this.mostrarMensaje(ServiciosSapFacade.MSG_ERROR_TECNICO, 'error');
      }
    } finally {
      this.guardando.set(false);
    }
  }

  // ==================== Baja ====================

  confirmarEliminar(servicio: ServicioSap): void {
    this.confirmationService.confirm({
      header: 'Confirmar eliminación',
      message: `¿Está seguro de que desea eliminar el servicio SAP ${servicio.cdservicio}?`,
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Eliminar',
      rejectLabel: 'Cancelar',
      acceptIcon: 'pi pi-trash',
      rejectIcon: 'pi pi-times',
      acceptButtonStyleClass: 'p-button-danger min-h-[44px]',
      rejectButtonStyleClass:
        'p-button-secondary p-button-outlined min-h-[44px]',
      accept: () => {
        void this.eliminar(servicio);
      }
    });
  }

  private async eliminar(servicio: ServicioSap): Promise<void> {
    this.loading.set(true);
    try {
      await this.repository.eliminar(servicio.cdservicio);
      this.reiniciarResultados();
      this.mostrarMensaje(ServiciosSapFacade.MSG_BORRADO_EXITOSO, 'success');
    } catch (error) {
      if (this.esConflicto(error)) {
        // Legacy: cons.errorServiceSapDeleteReg() — el registro sigue vigente y la tabla se conserva.
        this.mostrarMensaje(
          ServiciosSapFacade.MSG_SERVICIOS_ASOCIADOS,
          'error'
        );
      } else {
        this.reiniciarResultados();
        this.mostrarMensaje(ServiciosSapFacade.MSG_ERROR_TECNICO, 'error');
      }
    } finally {
      this.loading.set(false);
    }
  }

  // ==================== Utilidades ====================

  private camposInvalidos(
    modo: ServicioSapModoDialogo,
    cdservicio: string,
    dsservicio: string
  ): string[] {
    const campos: string[] = [];
    if (modo === 'crear' && !ServiciosSapFacade.REGEX_CODIGO.test(cdservicio)) {
      campos.push(ServiciosSapFacade.CAMPO_CODIGO);
    }
    if (
      dsservicio.length === 0 ||
      dsservicio.length > SERVICIO_SAP_DESCRIPCION_MAX_LENGTH ||
      !SERVICIO_SAP_DESCRIPCION_REGEX.test(dsservicio)
    ) {
      campos.push(ServiciosSapFacade.CAMPO_DESCRIPCION);
    }
    return campos;
  }

  /** Evita que el servidor rechace con 400 una descripción con caracteres fuera de la lista permitida. */
  private filtroValido(): boolean {
    const descripcion = sanitizeText(this.filtro.descripcion);
    if (SERVICIO_SAP_DESCRIPCION_REGEX.test(descripcion)) return true;
    this.reiniciarResultados();
    this.mostrarMensaje(
      `${ServiciosSapFacade.MSG_CAMPOS_INVALIDOS}: ${ServiciosSapFacade.CAMPO_DESCRIPCION}`,
      'warning'
    );
    return false;
  }

  private esConflicto(error: unknown): boolean {
    return (
      error instanceof HttpErrorResponse &&
      error.status === ServiciosSapFacade.HTTP_CONFLICT
    );
  }

  private mostrarMensaje(texto: string, tipo: ServicioSapMensajeTipo): void {
    this.mensaje.set(texto);
    this.mensajeTipo.set(tipo);
  }

  private limpiarMensajeDialogo(): void {
    this.mensajeDialogo.set('');
    this.mensajeDialogoTipo.set('warning');
  }

  /** Oculta la tabla y vuelve a la primera página, como el `getStListaServSap().clear()` del legacy. */
  private reiniciarResultados(): void {
    this.items.set([]);
    this.totalElements.set(0);
    this.busquedaRealizada.set(false);
    this.currentPage.set(1);
    this.hasNext.set(false);
    this.hasPrevious.set(false);
    this.nextCursorActual = null;
    this.previousCursorActual = null;
  }
}
